import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Container,
  Box,
  Typography,
  FormControl,
  InputLabel,
  FilledInput,
  InputAdornment,
  IconButton
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

import { useLocation } from 'react-router-dom';
import passwordService from '../services/PasswordService';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    background: theme.palette.background.default
  },
  card: {
    width: '100%',
    maxWidth: 400,
    padding: theme.spacing(4),
    background: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    boxShadow: theme.shadows[2],
    textAlign: 'center'
  },
  title: {
    marginBottom: theme.spacing(1),
    fontWeight: 600
  },
  description: {
    marginBottom: theme.spacing(3),
    color: theme.palette.text.secondary,
    fontSize: '0.95rem'
  },
  form: {
    width: '100%'
  },
  textField: {
    marginBottom: theme.spacing(2)
  },
  button: {
    marginTop: theme.spacing(2),
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 500
  },
  requirementList: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    textAlign: 'left'
  },
  requirementItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    fontSize: '0.9rem'
  },
  requirementBadge: {
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    fontSize: '0.7rem',
    fontWeight: 600
  }
}));

const PASSWORD_REQUIREMENTS = {
  minLength: 8,
  hasUppercase: /[A-Z]/,
  hasNumber: /[0-9]/,
  hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/
};

const validatePassword = (password) => {
  const minLength = password.length >= PASSWORD_REQUIREMENTS.minLength;
  const hasUppercase = PASSWORD_REQUIREMENTS.hasUppercase.test(password);
  const hasNumber = PASSWORD_REQUIREMENTS.hasNumber.test(password);
  const hasSpecialChar = PASSWORD_REQUIREMENTS.hasSpecialChar.test(password);

  return {
    minLength,
    hasUppercase,
    hasNumber,
    hasSpecialChar,
    isValid: minLength && hasUppercase && hasNumber && hasSpecialChar
  };
};

const ResetPassword = () => {
  const classes = useStyles();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordStrength = validatePassword(newPassword);
  const passwordsMatch = newPassword === confirmPassword && newPassword.length > 0;
  const isFormValid = passwordStrength.isValid && passwordsMatch;

  const { search } = useLocation();
  // eslint-disable-next-line no-undef
  const queryParams = new URLSearchParams(search);
  const resetToken = queryParams.get('resetToken');

  const history = useHistory();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!isFormValid) {
      setError('Please meet all password requirements');
      return;
    }

    try {
      setIsSubmitting(true);

      const resetPassObj = {
        resetToken: resetToken,
        newPassword: newPassword
      };

      await passwordService.resetPassword(resetPassObj);

      setSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      history.push('/login');
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to reset password. Please try again.';

      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box className={classes.container}>
      <Container maxWidth="sm">
        <Box className={classes.card}>
          <Typography variant="h6" className={classes.title}>
            Type Your New Password
          </Typography>

          <form onSubmit={handleSubmit} className={classes.form} noValidate>
            <FormControl variant="filled" margin="normal" fullWidth>
              <InputLabel htmlFor="new-password">New Password</InputLabel>
              <FilledInput
                id="new-password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            <Box className={classes.requirementList}>
              <Typography variant="subtitle2" color="textSecondary">
                Password requirements:
              </Typography>
              <RequirementItem met={passwordStrength.minLength} label="At least 8 characters" />
              <RequirementItem met={passwordStrength.hasUppercase} label="At least one uppercase letter (A-Z)" />
              <RequirementItem met={passwordStrength.hasNumber} label="At least one number (0-9)" />
              <RequirementItem
                met={passwordStrength.hasSpecialChar}
                label="At least one special character (!@#$%&*()?)"
              />
            </Box>

            <FormControl variant="filled" margin="normal" fullWidth>
              <InputLabel htmlFor="confirm-password">Confirm Password</InputLabel>
              <FilledInput
                id="confirm-password"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirm(!showConfirm)}
                      edge="end"
                    >
                      {showConfirm ? <Visibility /> : <VisibilityOff />}
                    </IconButton>
                  </InputAdornment>
                }
              />
            </FormControl>

            {error && (
              <Typography color="error" variant="body2" style={{ marginTop: 8 }}>
                {error}
              </Typography>
            )}

            {success && (
              <Typography variant="body2" style={{ color: 'green', marginTop: 8 }}>
                Password reset successfully!
              </Typography>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.button}
              disabled={isSubmitting || error}
            >
              {isSubmitting ? (
                <>
                  <CircularProgress size={20} style={{ marginRight: '8px' }} />
                  Processing...
                </>
              ) : (
                'Reset Password'
              )}
            </Button>
          </form>
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPassword;

function RequirementItem({ met, label }) {
  const classes = useStyles();
  return (
    <Box className={classes.requirementItem}>
      <Box
        className={classes.requirementBadge}
        style={{
          backgroundColor: met ? '#d4edda' : '#f5f5f5',
          color: met ? '#155724' : '#9e9e9e'
        }}
      >
        {met ? '✓' : '○'}
      </Box>
      <Typography variant="body2" color={met ? 'textPrimary' : 'textSecondary'}>
        {label}
      </Typography>
    </Box>
  );
}

RequirementItem.propTypes = {
  met: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired
};
